import { UISchemaElement } from '@jsonforms/core';
import CT from '@marketplace-ai/tags';
import { useSelector } from '@xstate/react';
import { ColDef } from 'ag-grid-community';
import { useState } from 'react';
import { EmptyState, FormDialog, TableCard } from '~/components';
import { BasicGrid } from '~/components/agGrid/Grid';
import { ACTIONS_COLUMN_BASE, ActionsRenderer, type ActionDef } from '~/components/Table/gridUtil';
import { PROVIDER_PAGE_KEYS } from '~/routing/routes';
import { useAttestationRef } from '../account/vaultSelectors';
import { attestationCols } from './constants';
import { JSONFormWrapper } from './JSONFormWrapper';

const TAGS = CT.attestations;

const AttestationsNoRowsOverlay = () => (
  <EmptyState
    pageKey={PROVIDER_PAGE_KEYS.attestations}
    title="No attestations yet"
    cyTag={TAGS.table.emptyState}
  />
);

export const AttestationDetail = () => {
  const { attestationRef, documentsService } = useAttestationRef();
  if (!attestationRef || !documentsService) {
    return null;
  }

  const { modal, formUi, form, attestations, loading, defaultData } = useSelector(
    attestationRef,
    (state) => state.context
  );

  const handleOpen = () => {
    attestationRef.send({ type: 'ON_GET_JSON_FORM', payload: 'attestations' });
  };

  const handleClose = () => {
    attestationRef.send({ type: 'ON_CLOSE_MODAL' });
  };

  const handleEdit = (id: number) => {
    attestationRef.send({ type: 'ON_EDIT', payload: { id } });
  };

  const handleDelete = (id: number) => {
    attestationRef.send({ type: 'ON_SEND_DELETE_CONFIRMATION', payload: { id } });
  };

  const handleDownload = (id: number) => {
    documentsService.send({ type: 'DOWNLOAD_DOCUMENT', id });
  };

  const [colDefs] = useState<ColDef[]>([
    { field: 'id', hide: true },
    ...attestationCols,
    {
      ...ACTIONS_COLUMN_BASE,
      width: 100,
      cellRenderer: ActionsRenderer,
      cellRendererParams: {
        actions: [
          {
            type: 'download',
            handler: handleDownload,
            cyTag: TAGS.table.downloadBtn,
            idField: 'documentInfoId',
            show: (data: { dateSubmitted: string; documentInfoId: number }) =>
              !!data.dateSubmitted && !!data.documentInfoId,
          },
          {
            type: 'edit',
            handler: handleEdit,
            cyTag: TAGS.table.editBtn,
            show: (data: { dateSubmitted: string }) => !data.dateSubmitted,
          },
          {
            type: 'delete',
            handler: handleDelete,
            cyTag: TAGS.table.deleteBtn,
            show: (data: { dateSubmitted: string }) => !data.dateSubmitted,
          },
        ] satisfies ActionDef[],
      },
    },
  ]);

  const incompleteAttestationCheck = attestations.some((attestation) => !attestation.dateSubmitted);

  return (
    <>
      <TableCard
        mobilePageHeader
        onAddClick={handleOpen}
        title="Attestations"
        addLabel="Add New Attestation"
        layoutTag={TAGS.layout}
        addBtnTag={TAGS.table.addBtn}
        disabledBtnTooltip={incompleteAttestationCheck}
        disabledTitle="You can only have one incomplete Attestation"
      >
        <BasicGrid
          data={attestations}
          loading={loading}
          colDefs={colDefs}
          cyTag={TAGS.grid}
          pagination={true}
          noRowsOverlayComponent={AttestationsNoRowsOverlay}
        />
      </TableCard>
      <FormDialog
        editing={false}
        creating={modal}
        onClose={handleClose}
        width={'4xl'}
        title={'Add an Attestation'}
      >
        <JSONFormWrapper
          actor={attestationRef}
          defaultValues={defaultData || {}}
          formDataSchema={form}
          layoutSchema={formUi as UISchemaElement}
        />
      </FormDialog>
    </>
  );
};
